/**
 * F-141 (REQ-041-001), decisione S4 del 2026-10-11: bozza automatica del modulo "Nuova pratica" in `localStorage`,
 * una chiave per utente. Logica pura (nessun React) per poterla provare: ogni accesso allo storage e' protetto,
 * se lo storage manca o e' pieno il modulo funziona come prima.
 */
export const DRAFT_KEY_PREFIX = 'crm.contratti.bozza.'
export const DRAFT_INTERVAL_MS = 10000

export interface StoredDraft<T> {
  form: T
  savedAt: string
}

/** Legge la bozza tenendo solo i campi noti (quelli di `empty`) e solo se testo: una bozza vecchia o guasta non rompe il modulo. */
export function readDraft<T extends object>(key: string, empty: T): StoredDraft<T> | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { form?: unknown; savedAt?: unknown } | null
    if (!parsed || typeof parsed !== 'object' || !parsed.form || typeof parsed.form !== 'object' || typeof parsed.savedAt !== 'string') return null
    const form = { ...empty }
    for (const k of Object.keys(empty) as (keyof T)[]) {
      const v = (parsed.form as Record<string, unknown>)[k as string]
      if (typeof v === 'string') form[k] = v as T[keyof T]
    }
    return { form, savedAt: parsed.savedAt }
  } catch {
    return null
  }
}

/** Scrive la bozza; `false` se lo storage e' pieno o bloccato (il modulo resta com'e', senza bozza). */
export function writeDraft<T>(key: string, form: T, savedAt: string): boolean {
  try {
    localStorage.setItem(key, JSON.stringify({ form, savedAt } satisfies StoredDraft<T>))
    return true
  } catch {
    return false
  }
}

export function removeDraft(key: string | null): void {
  if (!key) return
  try {
    localStorage.removeItem(key)
  } catch {
    /* storage non disponibile: niente da cancellare */
  }
}

/** Si scrive solo se il modulo e' cambiato rispetto all'ultima bozza e rispetto allo stato iniziale. */
export function shouldWriteDraft(snapshot: string, lastSnapshot: string, initialSnapshot: string): boolean {
  return snapshot !== lastSnapshot && snapshot !== initialSnapshot
}
